import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CenteredSpinner } from '../../components/ui/Spinner';
import type { PreviewSendBatch } from '../../types/previewSendBatch';
import { listBatchesForRollout } from './sendApi';

// Ad Preview Stage 3 §6.1. "Preview send history" -- recent send
// batches for a rollout, newest first. One row per batch with the
// rollup counts; "View report" navigates to the full-page preview
// report (/preview-sends/:batchId), which carries the recipient table,
// page-view metrics, and Retry affordances (Stage 6A v5 — replaced the
// old BatchDetailModal popup).
//
// Rendered as a section on the Rollout detail page. Refetches on focus
// (react-query default) and POLLS every 10s while any batch is still
// in_progress, so status transitions driven by the background worker +
// webhooks surface without a manual refresh. Polling stops cleanly once
// every batch is terminal (completed | partial) — required for async
// PREVIEW_WORKER_MODE where the HTTP response returns before the worker
// finishes, so the UI's only signal of progress is this poll.

type Props = {
  rolloutId: string;
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function batchToneClass(status: PreviewSendBatch['status']): string {
  switch (status) {
    case 'completed':
      return 'text-emerald-700';
    case 'partial':
      return 'text-amber-700';
    case 'in_progress':
    default:
      return 'text-slate-500';
  }
}

function batchStatusLabel(status: PreviewSendBatch['status']): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'partial':
      return 'Completed with failures';
    case 'in_progress':
    default:
      return 'In progress';
  }
}

export function SendStatusPanel({ rolloutId }: Props) {
  const batchesQuery = useQuery({
    queryKey: ['rolloutBatches', rolloutId],
    queryFn: () => listBatchesForRollout(rolloutId),
    // Poll every 10s while ANY batch is still in_progress; stop once every
    // batch is terminal (completed | partial). Mirrors BroadcastDetail.
    refetchInterval: (q) =>
      q.state.data?.some((b) => b.status === 'in_progress') ? 10_000 : false,
  });

  if (batchesQuery.isLoading) {
    return (
      <div className="py-4">
        <CenteredSpinner />
      </div>
    );
  }

  const batches = batchesQuery.data ?? [];

  return (
    <div className="space-y-3">
      {batches.length === 0 ? (
        <p className="text-sm text-slate-500">
          No previews have been sent for this rollout yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">By</th>
                <th className="px-4 py-2 font-medium">Channels</th>
                <th className="px-4 py-2 font-medium">Recipients</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">&nbsp;</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {batches.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-700">{formatWhen(b.initiatedAt)}</td>
                  <td className="px-4 py-2 text-slate-600">{b.initiatedByEmail}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {b.channels.map((c) => (c === 'email' ? '📧' : '📱')).join(' ')}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {b.totalRecipients} ({b.successCount}✓
                    {b.failureCount > 0 && ` · ${b.failureCount}⚠`}
                    {b.pendingCount > 0 && ` · ${b.pendingCount}⋯`})
                  </td>
                  <td className={`px-4 py-2 ${batchToneClass(b.status)}`}>
                    {batchStatusLabel(b.status)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/preview-sends/${b.id}`}
                      className="text-xs text-sky-700 hover:text-sky-900"
                    >
                      View report
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
